import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { isUUID } from 'validator';
import { AxiosError } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { parseISO } from 'date-fns';

import { GameModel } from '../types';
import toGameModel from '../services/toGameModel';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { defaultGcTime, defaultStaleTime } from '@/shared/constants';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import useGameTypeMetadataQuery from '@/shared/hooks/useGameTypeMetadataQuery';
import { formatPeriodTab } from '@/shared/utils/getPeriodName';
import type { GameTypeMetadataModel } from '@/features/autoIso/types/GameTypeMetadataModel';
import { AutoIsoObjectType } from '@/features/autoIso/types/AutoIsoModel';
import {
  CategorizedPlayer,
  CategoryPlayers,
  PlayerWithTeamInfo,
  ProcessedTeam,
} from '@/features/teams/types/AutoIso';
import { sortPlayers } from '@/features/teams/functions/sortPlayers';
import useRegistrationEntitiesQuery from '../../autoIso/hooks/useAutoIsoEntitiesQuery';
import { AutoIsoId } from '@/features/autoIso/types';
import { useSurfaceDimensions } from '@/features/autoIso/components/useSurfaceDimensions';

// Default game model when no data is available
const DEFAULT_GAME_MODEL: GameModel = {
  id: 0,
  name: '',
  status: '',
  startTimestamp: null,
  endTimestamp: null,
  externalid: '',
  awayTeam: {
    id: 0,
    name: undefined,
    displayName: undefined,
    abbreviation: undefined,
    externalid: undefined,
    leagueid: undefined,
    players: undefined,
    teamColors: undefined,
  },
  homeTeam: {
    id: 0,
    name: undefined,
    displayName: undefined,
    abbreviation: undefined,
    externalid: undefined,
    leagueid: undefined,
    players: undefined,
    teamColors: undefined,
  },
  awayTeamScore: 0,
  homeTeamScore: 0,
  uniqueId: '',
  homeTeamColor: '',
  awayTeamColor: '',
  available: false,
  enableTracking: false,
  trackingOffset: '',
  version: null,
  season: {
    league: {
      name: '',
    },
  },
  venue: {
    name: '',
    id: '',
    timezone: '',
    cameraSetup: undefined,
  },
};

// Define the context type
interface CurrentGameContextType {
  game: GameModel;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
  isFetching: boolean;
  gameId: string;
  gameLoaded: boolean;
  gameSwitched: boolean;
  homeTeamPrimaryColor: string;
  awayTeamPrimaryColor: string;
  awayTeamLogoPath: string;
  homeTeamLogoPath: string;
  gameTypeMetadata: GameTypeMetadataModel | null;
  parsePeriod: (period: number) => string;
  findPlayerById: (entityId: string | number) => PlayerWithTeamInfo | null;
  fullProcessedTeams: ProcessedTeam[];
  getFeaturedPlayers: (featuredIds: AutoIsoId[]) => PlayerWithTeamInfo[];
  surfaceWidth: number;
  surfaceHeight: number;
  surfaceName: string;
  objectOfPlay: string;
  isUsingVenueTimezone: boolean;
  toggleTimezone: () => void;
}

// Create the context with default values
const CurrentGameContext = createContext<CurrentGameContextType>({
  game: DEFAULT_GAME_MODEL,
  isLoading: false,
  isError: false,
  error: null,
  refetch: async () => ({}),
  isFetching: false,
  gameId: '',
  gameLoaded: false,
  gameSwitched: false,
  homeTeamPrimaryColor: '#000',
  awayTeamPrimaryColor: '#000',
  awayTeamLogoPath: '',
  homeTeamLogoPath: '',
  gameTypeMetadata: null,
  parsePeriod: (period: number) => `Period ${period}`,
  findPlayerById: () => null,
  fullProcessedTeams: [],
  getFeaturedPlayers: () => [],
  surfaceWidth: 0,
  surfaceHeight: 0,
  surfaceName: '',
  objectOfPlay: '',
  isUsingVenueTimezone: true,
  toggleTimezone: () => {},
});

// Query key generator for games
export const queryKey = (gameId: string) => ['Games', gameId] as const;

interface CurrentGameProviderProps {
  children: ReactNode;
}

export const CurrentGameProvider: React.FC<CurrentGameProviderProps> = ({ children }) => {
  const auth = useAuth();
  const gameId = useCurrentGameId();
  const queryClient = useQueryClient();
  const autoIsoQuery = useRegistrationEntitiesQuery();
  const { homeTeam, awayTeam, list = [] } = autoIsoQuery?.data || {};

  // Always use venue timezone (no switching allowed)
  const isUsingVenueTimezone = true;

  // No-op function kept for API compatibility
  const toggleTimezone = useCallback(() => {
    // Timezone switching is disabled - always use venue timezone
  }, []);

  // Processed teams without filtering by active status
  const fullProcessedTeams = useMemo((): ProcessedTeam[] => {
    return [homeTeam, awayTeam]
      .map((team) => {
        if (!team) return null;

        const teamPlayers = list.filter(
          (entity): entity is CategorizedPlayer =>
            entity.type === AutoIsoObjectType.Player && entity?.playerTeamId === team.id,
        );

        const categorizedPlayers = teamPlayers.reduce((acc, player) => {
          const category = player.playerCategory || 'Uncategorized';
          player.team = {
            id: team.id,
            displayName: team.displayName,
            abbreviation: team.abbreviation,
            colors: team.teamColors,
            logoUrl: getTeamLogoUrl(team) || null,
          };

          return {
            ...acc,
            [category]: [...(acc[category] || []), player],
          };
        }, {} as CategoryPlayers);

        // Sort players within each category
        Object.keys(categorizedPlayers).forEach((category) => {
          categorizedPlayers[category].sort(sortPlayers);
        });

        return {
          ...team,
          players: categorizedPlayers,
        };
      })
      .filter(Boolean) as ProcessedTeam[];
  }, [list, homeTeam, awayTeam]);

  // Game data query
  const {
    data: game = DEFAULT_GAME_MODEL,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    status: gameQueryStatus,
  } = useQuery({
    queryKey: queryKey(gameId),
    queryFn: async () => {
      if (!gameId || !isUUID(gameId)) {
        throw new Error('Invalid game ID');
      }

      const { data } = await get<GameModel>(`/v1.0/games/${gameId}`);
      return toGameModel(data);
    },
    retry: (failureCount, error) => {
      // Only retry for network errors, not for 404s or other client errors
      if (error instanceof AxiosError && (error.response?.status ?? 0) >= 500) {
        return failureCount < 3;
      }
      return false;
    },
    staleTime: defaultStaleTime,
    gcTime: defaultGcTime,
    refetchOnWindowFocus: Boolean(gameId) && isUUID(gameId),
    enabled: Boolean(gameId) && isUUID(gameId) && auth.sessionState === LocalSessionState.READY,
    select: (data) => data ?? DEFAULT_GAME_MODEL,
  });

  const gameTypeMetadataQuery = useGameTypeMetadataQuery();

  // Calculate the refetch interval based on the game's start time
  const calculateRefetchInterval = useCallback((startDate: Date): number => {
    const minutesUntilGame = (startDate.getTime() - Date.now()) / (1000 * 60);

    // Increasingly frequent polling as game time approaches
    if (minutesUntilGame > 1440) return 1000 * 60 * 60; // 1 hour if more than 24 hours away
    if (minutesUntilGame > 60) return 1000 * 60 * 10; // 10 minutes if 1-24 hours away
    if (minutesUntilGame > 10) return 1000 * 60; // 1 minute if 10-60 minutes away
    return 1000 * 3; // 3 seconds if less than 10 minutes away
  }, []);

  // Game status polling for upcoming games
  const updateGameStatus = useCallback(async () => {
    if (!gameId || !isUUID(gameId) || Number.isNaN(game?.id)) return {};

    const { data } = await get<GameModel>(`/v1.0/games/${gameId}?polling=true`);
    const polledGame = toGameModel(data);

    // Update the main game data in the cache if status has changed. This runs
    // here because v5 ignores useQuery's onSuccess, and only a real fetch may
    // write: a cached poll result from an earlier visit can be stale.
    if (polledGame.status !== game?.status) {
      queryClient.setQueryData(queryKey(gameId), polledGame);
    }
    return polledGame;
  }, [gameId, game?.id, game?.status, queryClient]);

  // Determine if the game is scheduled and has a valid start time
  const startDate = useMemo(() => {
    if (!game?.startTimestamp) return null;
    try {
      return parseISO(game.startTimestamp);
    } catch (e) {
      return null;
    }
  }, [game?.startTimestamp]);

  // Game status polling query
  useQuery({
    queryKey: ['GamesStatus', gameId],
    queryFn: updateGameStatus,
    refetchOnWindowFocus: true,
    enabled:
      Boolean(gameId) &&
      isUUID(gameId) &&
      game?.status === 'scheduled' &&
      Boolean(startDate) &&
      auth.sessionState === LocalSessionState.READY,
    refetchInterval: startDate ? calculateRefetchInterval(startDate) : undefined,
  });

  // Game switch query
  const { isSuccess: switchSucceeded, isFetching: switchFetching } = useQuery({
    queryKey: ['GameSwitch', gameId],
    queryFn: async () => {
      if (!gameId || !isUUID(gameId)) {
        throw new Error('Invalid game ID');
      }

      // Using the proper endpoint from your original implementation
      return await get<any>(`/v1.0/games/${gameId}/switch`);
    },
    retry: (failureCount, error: any) => {
      // Don't retry on session corruption errors
      if (error?.name === 'SessionCorruptionError' || error?.code === 'SESSION_CORRUPTED') {
        return false;
      }
      return failureCount < 2; // Retry twice for other errors
    },
    enabled: Boolean(gameId) && isUUID(gameId) && auth.sessionState === LocalSessionState.READY,
    refetchOnWindowFocus: false,
  });

  // Calculate team colors
  const homeTeamPrimaryColor = useMemo(
    () =>
      game?.homeTeamColor
        ? game.homeTeamColor
        : game?.homeTeam?.teamColors
          ? game.homeTeam.teamColors.find((tc) => tc.type === 'primary')?.hex_color || '#000'
          : '#000',
    [game?.homeTeamColor, game?.homeTeam?.teamColors],
  );

  const awayTeamPrimaryColor = useMemo(
    () =>
      game?.awayTeamColor
        ? game.awayTeamColor
        : game?.awayTeam?.teamColors
          ? game.awayTeam.teamColors.find((tc) => tc.type === 'primary')?.hex_color || '#000'
          : '#000',
    [game?.awayTeamColor, game?.awayTeam?.teamColors],
  );

  const homeTeamLogoPath = useMemo(() => getTeamLogoUrl(game?.homeTeam), [game?.homeTeam]);
  const awayTeamLogoPath = useMemo(() => getTeamLogoUrl(game?.awayTeam), [game?.awayTeam]);

  // Determine if the game is fully loaded
  const gameLoaded = useMemo(
    () => gameQueryStatus === 'success' && Boolean(game),
    [gameQueryStatus, game],
  );

  // The session's one stream serves this game only once its switch is done. A
  // cached success from an earlier visit doesn't count while it is redone: this
  // observer reports that refetch as fetching from the render the game changes.
  const gameSwitched = switchSucceeded && !switchFetching;

  const parsePeriod = useCallback(
    (period: number) => {
      const periodNamingMapping = gameTypeMetadataQuery.data?.periodNamingMapping?.[0];
      return formatPeriodTab(period, periodNamingMapping, game?.season?.seasonType);
    },
    [gameTypeMetadataQuery.data, game?.season?.seasonType],
  );

  const { surfaceWidth, surfaceHeight, surfaceName, objectOfPlay } =
    useSurfaceDimensions(gameTypeMetadataQuery);

  // Find Player by Entity ID
  const findPlayerById = useCallback(
    (entityId: string | number): PlayerWithTeamInfo | null => {
      // Convert to number if string
      const numericId = typeof entityId === 'string' ? parseInt(entityId, 10) : entityId;

      // Check if conversion was successful
      if (isNaN(numericId)) {
        return null;
      }

      // Find the entity in the list
      const entity = list.find((e) => e.id === numericId);

      if (!entity) {
        return null;
      }

      // If it's not a player, return the entity without team info
      if (entity.type !== AutoIsoObjectType.Player) {
        return {
          ...entity,
          id: numericId,
          team: null,
        };
      }

      // Find the team this player belongs to
      let team: {
        id: number;
        displayName?: string;
        abbreviation?: string;
        colors?: any;
        logoUrl?: string | null;
      } | null = null;

      if (entity.playerTeamId === homeTeam?.id && homeTeam) {
        team = {
          id: homeTeam.id,
          displayName: homeTeam.displayName,
          abbreviation: homeTeam.abbreviation,
          colors: homeTeam.teamColors,
          logoUrl: getTeamLogoUrl(homeTeam) || null,
        };
      } else if (entity.playerTeamId === awayTeam?.id && awayTeam) {
        team = {
          id: awayTeam.id,
          displayName: awayTeam.displayName,
          abbreviation: awayTeam.abbreviation,
          colors: awayTeam.teamColors,
          logoUrl: getTeamLogoUrl(awayTeam) || null,
        };
      }

      // Return entity with team info
      return {
        ...entity,
        id: numericId,
        team,
      };
    },
    [list, homeTeam, awayTeam],
  );

  const getFeaturedPlayers = useCallback(
    (featuredIds: AutoIsoId[]): PlayerWithTeamInfo[] => {
      const featuredPlayers: PlayerWithTeamInfo[] = [];

      // Iterate through both teams to find featured players
      [homeTeam, awayTeam].forEach((team) => {
        if (!team) return;
        const featuredPlayersFromTeams = list.filter(
          (entity) =>
            //@ts-expect-error
            entity.playerTeamId === team.id &&
            featuredIds
              .map((id) => {
                //@ts-expect-error
                return parseInt(id, 10);
              })
              //@ts-expect-error
              .includes(entity.id),
        );

        featuredPlayersFromTeams.forEach((player) => {
          if (player) {
            featuredPlayers.push({
              ...player,
              id: typeof player.id === 'string' ? parseInt(player.id, 10) : player.id,
              team: {
                id: team.id,
                displayName: team.displayName,
                abbreviation: team.abbreviation,
                colors: team.teamColors,
                logoUrl: getTeamLogoUrl(team) || null,
              },
            });
          }
        });
      });

      return featuredPlayers;
    },
    [list, homeTeam, awayTeam],
  );

  // Create the context value
  const contextValue = useMemo(
    () => ({
      game,
      isLoading,
      isError,
      error,
      refetch,
      isFetching,
      gameId,
      gameLoaded,
      gameSwitched,
      homeTeamPrimaryColor,
      awayTeamPrimaryColor,
      awayTeamLogoPath,
      homeTeamLogoPath,
      gameTypeMetadata: gameTypeMetadataQuery.data ?? null,
      parsePeriod,
      findPlayerById,
      fullProcessedTeams,
      getFeaturedPlayers,
      surfaceWidth,
      surfaceHeight,
      surfaceName,
      objectOfPlay,
      isUsingVenueTimezone,
      toggleTimezone,
    }),
    [
      game,
      isLoading,
      isError,
      error,
      refetch,
      isFetching,
      gameId,
      gameLoaded,
      gameSwitched,
      homeTeamPrimaryColor,
      awayTeamPrimaryColor,
      awayTeamLogoPath,
      homeTeamLogoPath,
      gameTypeMetadataQuery.data,
      parsePeriod,
      fullProcessedTeams,
      findPlayerById,
      getFeaturedPlayers,
      surfaceWidth,
      surfaceHeight,
      surfaceName,
      objectOfPlay,
      isUsingVenueTimezone,
      toggleTimezone,
    ],
  );

  return <CurrentGameContext.Provider value={contextValue}>{children}</CurrentGameContext.Provider>;
};

// Create a hook to use the current game context
export const useCurrentGame = () => {
  const context = useContext(CurrentGameContext);
  if (context === undefined) {
    throw new Error('useCurrentGame must be used within a CurrentGameProvider');
  }
  return context;
};

export default CurrentGameContext;
